import React, { useState } from "react";
import { Link } from "react-router-dom";
import { X, Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { setAuthSessionCookies } from "@/lib/authCookies";
import { mapTrialSignupError, registerTrialAccount, TRIAL_BOT_TYPES } from "@/lib/landingSignup";
import { validateEmail } from "./utils";
import {
  SIGNUP_TITLE,
  SIGNUP_SUBTITLE,
  SIGNUP_SUBMIT,
  SIGNUP_MICRO,
  SIGNUP_SUCCESS_TITLE,
  SIGNUP_SUCCESS_BODY,
  SIGNUP_OPEN_DASHBOARD,
  SIGNUP_EXISTING_EMAIL,
} from "./signupContent";

const SignupModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");

  if (!isOpen) return null;

  const resetState = () => {
    setEmail("");
    setError("");
    setTouched(false);
    setIsLoading(false);
    setIsSuccess(false);
    setRegisteredEmail("");
  };

  const handleClose = () => {
    if (isLoading) return;
    resetState();
    onClose();
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    if (touched && value) {
      if (validateEmail(value)) {
        setError("");
      } else {
        setError("Please enter a valid email address");
      }
    } else if (touched && !value) {
      setError("Email is required");
    }
  };

  const handleBlur = () => {
    setTouched(true);
    if (!email) {
      setError("Email is required");
    } else if (!validateEmail(email)) {
      setError("Please enter a valid email address");
    } else {
      setError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);

    if (!email) {
      setError("Email is required");
      return;
    }
    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const data = await registerTrialAccount({
        email,
        botType: TRIAL_BOT_TYPES.ROOFING,
      });

      if (data.status === "ok") {
        const token = data.token;
        const botType = data.bot_type || TRIAL_BOT_TYPES.ROOFING;
        const normalizedEmail = email.toLowerCase();

        setAuthSessionCookies({
          token,
          email: normalizedEmail,
          accountType: data.account_type || "client",
          botType,
        });

        setRegisteredEmail(normalizedEmail);
        setIsSuccess(true);
        setIsLoading(false);
      } else if (data.status === "error") {
        if (data.message === "The user already exists") {
          setError(SIGNUP_EXISTING_EMAIL);
        } else {
          setError(data.message || "An error occurred. Please try again.");
        }
        setIsLoading(false);
      } else {
        setError("Unexpected response from the server. Please try again.");
        setIsLoading(false);
      }
    } catch (err) {
      console.error("Roofing trial registration error:", err);
      setError(mapTrialSignupError(err, SIGNUP_EXISTING_EMAIL));
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      handleClose();
    }
  };

  const isValid = email && validateEmail(email);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4 py-6 overflow-y-auto"
      onClick={handleBackdropClick}
      data-testid="signup-modal-overlay"
    >
      <div
        className="relative w-full max-w-md glass rounded-2xl p-8 border border-orange-500/20 animate-fade-in-up my-auto"
        data-testid="signup-modal"
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors disabled:opacity-50"
          data-testid="signup-close-button"
          aria-label="Close popup"
          disabled={isLoading}
        >
          <X className="w-5 h-5" />
        </button>

        {!isSuccess ? (
          <div data-testid="signup-modal-form">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-[#F3F4F6] text-center mb-2 pr-6">
              {SIGNUP_TITLE}
            </h2>
            <p
              className="font-body text-sm text-[#9CA3AF] text-center leading-relaxed mb-6 pr-6"
              data-testid="signup-modal-subtitle"
            >
              {SIGNUP_SUBTITLE}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="signup-email-input" className="block text-[#9CA3AF] text-sm mb-2 font-body">
                  Email
                </label>
                <input
                  id="signup-email-input"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleBlur}
                  placeholder="your@email.com"
                  disabled={isLoading}
                  className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body disabled:opacity-50 ${
                    error && touched
                      ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
                      : isValid
                      ? "border-green-500/50 focus:border-green-500 focus:ring-1 focus:ring-green-500/50"
                      : "border-white/10 focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50"
                  }`}
                  data-testid="signup-email-input"
                />
                {error && touched && (
                  <p className="mt-2 text-red-400 text-xs font-body" data-testid="signup-email-error">
                    {error}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={!isValid || isLoading}
                className={`w-full font-heading font-bold text-base md:text-lg py-4 px-6 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 ${
                  isValid && !isLoading
                    ? "btn-pulse-fiery bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-400 hover:to-red-500"
                    : "bg-orange-500/50 text-white/70 cursor-not-allowed"
                }`}
                data-testid="signup-submit"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Creating your account...
                  </>
                ) : (
                  <>
                    {SIGNUP_SUBMIT}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              <p className="text-[#9CA3AF]/70 text-xs text-center pt-2">{SIGNUP_MICRO}</p>
            </form>
          </div>
        ) : (
          <div data-testid="signup-modal-success">
            <div className="flex items-center justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-green-500/15 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-400" />
              </div>
            </div>
            <h2 className="font-heading font-bold text-xl md:text-2xl text-[#F3F4F6] text-center mb-4 pr-6">
              {SIGNUP_SUCCESS_TITLE}
            </h2>
            <p className="font-body text-sm md:text-[15px] text-[#D1D5DB] text-center leading-relaxed">
              {SIGNUP_SUCCESS_BODY(registeredEmail)}
            </p>

            <Link
              to="/dashboard"
              className="mt-6 w-full font-heading font-bold text-base md:text-lg py-4 px-6 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 btn-pulse-fiery bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-400 hover:to-red-500"
              data-testid="signup-open-dashboard"
              onClick={() => {
                resetState();
                onClose();
              }}
            >
              {SIGNUP_OPEN_DASHBOARD}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignupModal;
